// Helpers for the blog article template (app/blog/[slug]).
//
// The design's left rail is an "On This Page" list that scrolls to the
// article's own headings, and its chips carry a reading time and an updated
// month. None of that is stored — the body is a single HTML blob in
// website_media_items — so all three are derived from the body here, server
// side, before it reaches the client.

export type Heading = {
  id: string;
  text: string;
  level: 2 | 3;
  /** What the row points at. Media sits under the heading it follows, so the
   *  rail reads as a contents list rather than a flat jumble of links. */
  kind?: "heading" | "figure" | "video";
};

const TAG = /<[^>]*>/g;
const ENTITY = /&(#\d+|#x[0-9a-f]+|[a-z]+);/gi;

const NAMED: Record<string, string> = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ",
  rsquo: "’", lsquo: "‘", rdquo: "”", ldquo: "“",
  mdash: "—", ndash: "–", hellip: "…",
};

/** HTML → plain text, entities resolved. Used for word counts and for the
 *  heading labels shown in the rail. */
export function htmlToText(html: string): string {
  return html
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, " ")
    .replace(TAG, " ")
    .replace(ENTITY, (m, code: string) => {
      if (code.startsWith("#x") || code.startsWith("#X")) {
        return String.fromCodePoint(parseInt(code.slice(2), 16));
      }
      if (code.startsWith("#")) return String.fromCodePoint(parseInt(code.slice(1), 10));
      return NAMED[code.toLowerCase()] ?? m;
    })
    .replace(/\s+/g, " ")
    .trim();
}

/** Whole minutes at 200 wpm, floored at 1 so a short post never reads
 *  "0 Min read". The design writes the chip as "[X] Min read". */
export function readingMinutes(html: string | null): number {
  if (!html) return 1;
  const words = htmlToText(html).split(" ").filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

function slugify(text: string, taken: Set<string>): string {
  const base =
    text
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 60) || "section";
  let id = base;
  let n = 2;
  // Imported posts repeat headings ("Overview" under several sections), and a
  // duplicate id would send every rail link to the first one.
  while (taken.has(id)) id = `${base}-${n++}`;
  taken.add(id);
  return id;
}

const attrOf = (attrs: string, name: string): string | null => {
  const m = attrs.match(new RegExp(`\\s${name}\\s*=\\s*["']([^"']*)["']`, "i"));
  return m && m[1].trim() ? m[1].trim() : null;
};

/** Alt text is often a filename ("flexible-office-market-growth-statistics").
 *  Give it spaces and a capital so the rail reads as a label, not a slug. */
const prettify = (s: string) =>
  s.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim().replace(/^./, (c) => c.toUpperCase());

/** Add an id to an element's OPENING tag, leaving the rest of it alone. */
const withId = (whole: string, tag: string, id: string) =>
  whole.replace(new RegExp(`^<${tag}`, "i"), `<${tag} id="${id}"`);

// One pass in document order over the headings AND the media worth listing.
// The figure alternative comes first so a captioned figure is taken whole and
// its inner <img> is never counted a second time.
const NODE = new RegExp(
  [
    String.raw`<(h[23])([^>]*)>([\s\S]*?)<\/\1>`,
    String.raw`<figure([^>]*)>([\s\S]*?)<\/figure>`,
    String.raw`<iframe([^>]*)>([\s\S]*?)<\/iframe>`,
    String.raw`<video([^>]*)>([\s\S]*?)<\/video>`,
    String.raw`<img([^>]*?)\/?>`,
  ].join("|"),
  "gi",
);

/**
 * Gives every h2/h3 — and every infographic or video worth linking to — an id,
 * and returns the rail's items alongside in document order.
 *
 * An id already on the element is kept, so hand-authored anchors and any
 * inbound links to them keep working.
 *
 * Media is listed only when it carries its own label: a figcaption, alt text,
 * or an iframe/video title. Unlabelled images are skipped deliberately — the
 * centre-story posts run to eight or more decorative photographs with no alt
 * between them, and eight rows reading "Infographic" would bury the headings
 * the rail exists to show. A video with no title is still listed, because a
 * post rarely holds more than one and missing it is worse than a plain label.
 */
export function withHeadingIds(html: string): { html: string; headings: Heading[] } {
  const headings: Heading[] = [];
  const taken = new Set<string>();

  const push = (
    whole: string,
    tag: string,
    attrs: string,
    label: string,
    kind: "figure" | "video",
  ) => {
    const existing = attrOf(attrs, "id");
    if (existing) {
      taken.add(existing);
      headings.push({ id: existing, text: label, level: 3, kind });
      return whole;
    }
    const id = slugify(`${kind}-${label}`, taken);
    headings.push({ id, text: label, level: 3, kind });
    return withId(whole, tag, id);
  };

  const out = html.replace(
    NODE,
    (
      match: string,
      hTag: string, hAttrs: string, hInner: string,
      figAttrs: string, figInner: string,
      ifrAttrs: string, ifrInner: string,
      vidAttrs: string, vidInner: string,
      imgAttrs: string,
    ) => {
      if (hTag !== undefined) {
        const text = htmlToText(hInner);
        if (!text) return match;                    // blank headings stay out of the rail
        const level = hTag.toLowerCase() === "h2" ? 2 : 3;
        const existing = attrOf(hAttrs, "id");
        if (existing) {
          taken.add(existing);
          headings.push({ id: existing, text, level: level as 2 | 3, kind: "heading" });
          return match;
        }
        const id = slugify(text, taken);
        headings.push({ id, text, level: level as 2 | 3, kind: "heading" });
        return `<${hTag}${hAttrs} id="${id}">${hInner}</${hTag}>`;
      }

      if (figAttrs !== undefined) {
        const caption = figInner.match(/<figcaption[^>]*>([\s\S]*?)<\/figcaption>/i);
        const innerImg = figInner.match(/<img([^>]*?)\/?>/i);
        const label =
          (caption && htmlToText(caption[1])) ||
          (innerImg && attrOf(innerImg[1], "alt") && prettify(attrOf(innerImg[1], "alt")!)) ||
          null;
        const isVideo = /<(iframe|video)[\s>]/i.test(figInner);
        if (!label && !isVideo) return match;
        return push(match, "figure", figAttrs, label || "Video", isVideo ? "video" : "figure");
      }

      if (ifrAttrs !== undefined) {
        const label = attrOf(ifrAttrs, "title");
        return push(match, "iframe", ifrAttrs, label ? prettify(label) : "Video", "video");
      }

      if (vidAttrs !== undefined) {
        const label = attrOf(vidAttrs, "title") || attrOf(vidAttrs, "aria-label");
        return push(match, "video", vidAttrs, label ? prettify(label) : "Video", "video");
      }

      if (imgAttrs !== undefined) {
        const alt = attrOf(imgAttrs, "alt");
        if (!alt) return match;                     // decorative — not a rail entry
        return push(match, "img", imgAttrs, prettify(alt), "figure");
      }

      return match;
    },
  );

  return { html: out, headings };
}

/** "Updated : March 2026" — the design's chip shows month and year only. */
export function fmtMonthYear(d: string | null): string | null {
  if (!d) return null;
  const parsed = new Date(`${d}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Drops a leading line from an excerpt when it merely restates the title.
 *
 *  Many posts store their excerpt as "<headline>\n<summary>", so a card that
 *  prints the title and the excerpt together shows the headline twice. The
 *  restatement is rarely verbatim — "How GCC Expansion Is Redefining Global
 *  Business Strategy" is stored with a first line of "GCC Expansion:
 *  Redefining Global Business Strategy" — so an equality check misses it.
 *
 *  Three conditions have to hold before a line is dropped, which is what
 *  keeps a genuine opening sentence safe:
 *    1. there is a second line, so the first reads as a standalone heading;
 *    2. it carries no sentence-ending punctuation;
 *    3. at least 80% of its words also appear in the title.
 *
 *  A real sentence that happened to reuse the title's words would normally
 *  fail (2), and one introducing any new noun fails (3).
 */
export function excerptWithoutTitle(
  excerpt: string | null | undefined,
  title: string | null | undefined,
): string {
  const text = (excerpt ?? "").trim();
  if (!text || !title) return text;

  const nl = text.search(/\r?\n/);
  if (nl === -1) return text;

  const first = text.slice(0, nl).trim();
  const rest = text.slice(nl).trim();
  if (!first || !rest) return text;
  if (/[.!?…]$/.test(first)) return text;

  const words = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);

  const firstWords = words(first);
  if (!firstWords.length) return text;
  const titleWords = new Set(words(title));
  const shared = firstWords.filter((w) => titleWords.has(w)).length;

  return shared / firstWords.length >= 0.8 ? rest : text;
}
